import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  useToast,
  type Column,
} from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'

type Schedule = components['schemas']['ScheduleRead']
type MembershipType = components['schemas']['MembershipTypeRead']
type ChargeSummary = components['schemas']['ChargeSummaryRead']

const DONEM: Record<Schedule['period'], string> = {
  yearly: 'Yıllık',
  quarterly: 'Üç aylık',
  monthly: 'Aylık',
}

/** Kuruş → okunur TL. Para her yerde kuruş (ADR-3); biçimleme yalnızca burada. */
export function tl(kurus: number): string {
  return (kurus / 100).toLocaleString('tr', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits: 2,
  })
}

/**
 * Aidat tarifeleri ve borçlandırma — YES-106.
 *
 * Borçlandırma zaten her gün otomatik koşuyor; buradaki düğme yeni tarife
 * girildikten sonra sonucu beklemeden görmek için. İş idempotent olduğu için
 * elle tetiklemek zararsız.
 */
export function Aidat() {
  const slug = useTenantSlug()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [yeniAcik, setYeniAcik] = useState(false)
  const [sonKosu, setSonKosu] = useState<ChargeSummary | null>(null)

  const tarifeler = useQuery({
    queryKey: ['fee-schedules', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/fees/schedules')
      return data ?? []
    },
  })

  const tipler = useQuery({
    queryKey: ['membership-types', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/members/types')
      return data ?? []
    },
  })

  const kosu = useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/fees/charges/run', { body: {} })
      return data
    },
    onSuccess: (ozet) => {
      setSonKosu(ozet ?? null)
      void queryClient.invalidateQueries({ queryKey: ['members', slug] })
      toast({
        title: ozet?.created ? `${ozet.created} borç oluşturuldu` : 'Yeni borç oluşmadı',
        tone: 'success',
      })
    },
    onError: (error) =>
      toast({
        title: 'Borçlandırma çalıştırılamadı',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const tipAdi = (id: string) => tipler.data?.find((tip) => tip.id === id)?.name ?? 'Bilinmeyen tip'

  const kolonlar: Column<Schedule>[] = [
    {
      key: 'name',
      header: 'Tarife',
      cell: (row) => <span className="font-medium">{row.name}</span>,
      value: (row) => row.name,
    },
    {
      key: 'type',
      header: 'Üyelik tipi',
      cell: (row) => tipAdi(row.membership_type_id),
      value: (row) => tipAdi(row.membership_type_id),
    },
    {
      key: 'period',
      header: 'Dönem',
      cell: (row) => DONEM[row.period],
      value: (row) => DONEM[row.period],
    },
    {
      key: 'amount',
      header: 'Tutar',
      align: 'right',
      cell: (row) => <span className="tabular-nums">{tl(row.amount)}</span>,
      value: (row) => row.amount,
    },
    {
      key: 'valid',
      header: 'Geçerlilik',
      cell: (row) => (
        <span className="text-[var(--ad-text-muted)]">
          {row.valid_from}
          {row.valid_to ? ` – ${row.valid_to}` : ' –'}
        </span>
      ),
      value: (row) => row.valid_from,
    },
    {
      key: 'active',
      header: 'Durum',
      cell: (row) =>
        row.is_active ? <Badge tone="success">Etkin</Badge> : <Badge tone="neutral">Kapalı</Badge>,
      value: (row) => String(row.is_active),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold">Aidat</h1>
        <div className="ml-auto flex gap-2">
          <Button variant="secondary" disabled={kosu.isPending} onClick={() => kosu.mutate()}>
            <Icon name="refresh-cw" size={16} aria-hidden />
            {kosu.isPending ? 'Çalışıyor…' : 'Borçlandırmayı çalıştır'}
          </Button>
          <Button onClick={() => setYeniAcik(true)}>
            <Icon name="plus" size={16} aria-hidden />
            Tarife ekle
          </Button>
        </div>
      </div>

      {sonKosu && <KosuOzeti ozet={sonKosu} />}

      <Card>
        <CardHeader
          title="Tarifeler"
          description="Zam yapmak için yeni tarife eklenir; eskisi geçmiş borçlandırmaların dayanağı olarak kalır."
        />
        <CardBody>
          {tarifeler.data && tarifeler.data.length > 0 ? (
            <DataTable
              rows={tarifeler.data}
              columns={kolonlar}
              rowKey={(row) => row.id}
              searchPlaceholder="Tarife ara…"
              exportFileName="aidat-tarifeleri"
            />
          ) : (
            <EmptyState
              icon={<Icon name="wallet" size={24} aria-hidden />}
              title="Tarife tanımlı değil"
              description="Aidat borçlandırması, üyelik tipi başına bir tarife tanımlanınca başlar."
              action={<Button onClick={() => setYeniAcik(true)}>Tarife ekle</Button>}
            />
          )}
        </CardBody>
      </Card>

      <YeniTarife acik={yeniAcik} onAcik={setYeniAcik} tipler={tipler.data ?? []} slug={slug} />
    </div>
  )
}

/**
 * Koşu dökümü. Atlananlar SEBEBİYLE ayrı gösteriliyor: "200 üyeden 3'ü
 * borçlandırıldı" tek başına alarm mı normal mi belli değil.
 */
function KosuOzeti({ ozet }: { ozet: ChargeSummary }) {
  const satirlar = [
    { label: 'Oluşturuldu', value: ozet.created, tone: 'success' as const },
    { label: 'Zaten vardı', value: ozet.skipped_already_charged, tone: 'neutral' as const },
    { label: 'Tarifesi yok', value: ozet.skipped_no_schedule, tone: 'warning' as const },
    { label: 'Aidattan muaf', value: ozet.skipped_exempt, tone: 'neutral' as const },
    { label: 'Üyeliği başlamamış', value: ozet.skipped_not_started, tone: 'neutral' as const },
  ].filter((satir) => satir.value > 0)

  return (
    <Card>
      <CardBody className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-[var(--ad-text-muted)]">Son koşu:</span>
        {satirlar.length === 0 ? (
          <span>Değerlendirilecek üye bulunamadı.</span>
        ) : (
          satirlar.map((satir) => (
            <Badge key={satir.label} tone={satir.tone}>
              {satir.label}: {satir.value}
            </Badge>
          ))
        )}
      </CardBody>
    </Card>
  )
}

function YeniTarife({
  acik,
  onAcik,
  tipler,
  slug,
}: {
  acik: boolean
  onAcik: (acik: boolean) => void
  tipler: MembershipType[]
  slug: string | null
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [hatalar, setHatalar] = useState<Record<string, string>>({})

  const kaydet = useMutation({
    mutationFn: async (form: FormData) => {
      // Kullanıcı TL giriyor, sunucu kuruş bekliyor (ADR-3). Çevrim TEK
      // noktada: her ekranda tekrarlanırsa biri er geç unutulur.
      const lira = Number(String(form.get('amount')).replace(',', '.'))
      await api.POST('/api/v1/fees/schedules', {
        body: {
          membership_type_id: String(form.get('membership_type_id')),
          name: String(form.get('name')),
          amount: Math.round(lira * 100),
          period: form.get('period') as Schedule['period'],
          valid_from: String(form.get('valid_from') || '') || undefined,
        },
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fee-schedules', slug] })
      onAcik(false)
      setHatalar({})
      toast({ title: 'Tarife eklendi', tone: 'success' })
    },
    onError: (error) => {
      if (error instanceof ApiError) setHatalar(error.fieldErrors())
      toast({
        title: 'Tarife eklenemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      })
    },
  })

  return (
    <Dialog
      open={acik}
      onOpenChange={onAcik}
      title="Aidat tarifesi ekle"
      description="Geçerlilik başlangıcından itibaren bu tarife uygulanır; öncekiler geçmiş için saklanır."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          kaydet.mutate(new FormData(event.currentTarget))
        }}
      >
        <Field label="Tarife adı" required error={hatalar.name}>
          {(props) => <Input name="name" placeholder="2026 Yıllık Aidat" autoFocus {...props} />}
        </Field>

        <Field label="Üyelik tipi" required error={hatalar.membership_type_id}>
          {(props) => (
            <select
              name="membership_type_id"
              className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
              {...props}
            >
              {tipler.map((tip) => (
                <option key={tip.id} value={tip.id}>
                  {tip.name}
                </option>
              ))}
            </select>
          )}
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Tutar (TL)" required error={hatalar.amount}>
            {(props) => <Input name="amount" inputMode="decimal" placeholder="500" {...props} />}
          </Field>
          <Field label="Dönem" required error={hatalar.period}>
            {(props) => (
              <select
                name="period"
                defaultValue="yearly"
                className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
                {...props}
              >
                {Object.entries(DONEM).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>

        <Field
          label="Geçerlilik başlangıcı"
          error={hatalar.valid_from}
          hint="Boş bırakılırsa bugünden itibaren geçerli olur."
        >
          {(props) => <Input name="valid_from" type="date" {...props} />}
        </Field>

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => onAcik(false)}>
            Vazgeç
          </Button>
          <Button type="submit" disabled={kaydet.isPending || tipler.length === 0}>
            {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
          </Button>
        </div>

        {tipler.length === 0 && (
          <p className="text-xs text-[var(--ad-danger)]">
            Önce en az bir üyelik tipi tanımlanmalı.
          </p>
        )}
      </form>
    </Dialog>
  )
}
